import React, { useCallback } from 'react';
import dayjs from 'dayjs';
import { CONTRACT_TYPES } from '../../../utils/contractTypes';

const ALL = 'all';

const CHANNEL_OPTIONS = [
    { label: 'Todos los canales', value: ALL },
    { label: 'Moderno', value: 'moderno' },
    { label: 'Tradicional', value: 'tradicional' }
];

/**
 * Estado de la barra de filtros de contrataciones. Los filtros de canal / cliente / PDV se
 * aplican sobre los pisos de venta asignados, de modo que la inversión total siempre se
 * calcula sobre lo filtrado (ver `ContractsController.buildContractRows`).
 */
export function useContractFilters({ onFilter, stores, clients }) {
    const [searchValue, setSearchValue] = React.useState('');
    const [channel, setChannel] = React.useState(ALL);
    const [clientId, setClientId] = React.useState(ALL);
    const [storeId, setStoreId] = React.useState(ALL);
    const [type, setType] = React.useState(ALL);

    const clientOptions = React.useMemo(() => {
        const filtered = (clients || []).filter((client) => channel === ALL || client.channel === channel);
        return [
            { label: 'Todos los clientes', value: ALL },
            ...filtered.map((client) => ({ label: client.name, value: client._id })).sort((a, b) => a.label.localeCompare(b.label))
        ];
    }, [clients, channel]);

    const storeOptions = React.useMemo(() => {
        const clientById = new Map((clients || []).map((client) => [String(client._id), client]));
        const filtered = (stores || []).filter((store) => {
            const client = clientById.get(String(store.client));
            if (channel !== ALL && client?.channel !== channel) return false;
            if (clientId !== ALL && String(store.client) !== String(clientId)) return false;
            return true;
        });
        return [
            { label: 'Todos los pisos de venta', value: ALL },
            ...filtered.map((store) => ({ label: store.name, value: store._id })).sort((a, b) => a.label.localeCompare(b.label))
        ];
    }, [stores, clients, channel, clientId]);

    const typeOptions = React.useMemo(
        () => [{ label: 'Todos los tipos', value: ALL }, ...CONTRACT_TYPES.map(({ label, value }) => ({ label, value }))],
        []
    );

    // Reset de filtros dependientes cuando cambia el nivel superior
    const onChannelChange = (value) => {
        setChannel(value);
        setClientId(ALL);
        setStoreId(ALL);
        onFilter({ search: searchValue, channel: value, clientId: ALL, storeId: ALL, type });
    };

    const onClientChange = (value) => {
        setClientId(value);
        setStoreId(ALL);
        onFilter({ search: searchValue, channel, clientId: value, storeId: ALL, type });
    };

    const onStoreChange = (value) => {
        setStoreId(value);
        onFilter({ search: searchValue, channel, clientId, storeId: value, type });
    };

    const onTypeChange = (value) => {
        setType(value);
        onFilter({ search: searchValue, channel, clientId, storeId, type: value });
    };

    const onSearch = (value) => {
        const search = value ?? '';
        setSearchValue(search);
        onFilter({ search, channel, clientId, storeId, type });
    };

    const onClear = () => {
        setSearchValue('');
        setChannel(ALL);
        setClientId(ALL);
        setStoreId(ALL);
        setType(ALL);
        onFilter({ search: '', channel: ALL, clientId: ALL, storeId: ALL, type: ALL });
    };

    const hasActiveFilters = Boolean(searchValue) || channel !== ALL || clientId !== ALL || storeId !== ALL || type !== ALL;

    return {
        searchValue,
        channel,
        clientId,
        storeId,
        type,
        channelOptions: CHANNEL_OPTIONS,
        clientOptions,
        storeOptions,
        typeOptions,
        onChannelChange,
        onClientChange,
        onStoreChange,
        onTypeChange,
        onSearch,
        onClear,
        hasActiveFilters
    };
}

/** Conteos de estado de las contrataciones visibles. */
export function useContractStats(contracts) {
    return React.useMemo(() => {
        const totalContracts = contracts?.length || 0;
        const activeContracts = contracts?.filter((c) => c.active && !dayjs(c.expirationDate).isBefore(dayjs(), 'day'))?.length || 0;
        const expiredContracts = contracts?.filter((c) => dayjs(c.expirationDate).isBefore(dayjs(), 'day'))?.length || 0;
        const inactiveContracts = contracts?.filter((c) => !c.active)?.length || 0;

        return { totalContracts, activeContracts, expiredContracts, inactiveContracts };
    }, [contracts]);
}

/** Estado y envío del drawer de crear / editar contratación. */
export function useContractForm({ isOpen, setIsOpen, createContract, updateContract, stores, editMode, contractData }) {
    const [name, setName] = React.useState('');
    const [contractType, setContractType] = React.useState('cabezal');
    const [loading, setLoading] = React.useState(false);
    const [isStoreModalOpen, setIsStoreModalOpen] = React.useState(false);
    const [selectedStores, setSelectedStores] = React.useState([]);
    const [expirationDate, setExpirationDate] = React.useState('');
    const [price, setPrice] = React.useState(0);

    // Populate fields when editing
    React.useEffect(() => {
        if (editMode && contractData) {
            setName(contractData.name || '');
            setContractType(contractData.type || 'cabezal');
            setSelectedStores(contractData.stores?.map((s) => (typeof s === 'string' ? s : s._id)) || []);
            setExpirationDate(contractData.expirationDate ? dayjs(contractData.expirationDate).format('YYYY-MM-DD') : '');
            setPrice(contractData.price ?? 0);
        } else {
            // Reset form when not editing
            setName('');
            setContractType('cabezal');
            setSelectedStores([]);
            setExpirationDate('');
            setPrice(0);
        }
    }, [editMode, contractData, isOpen]);

    const filteredStores = React.useMemo(() => stores?.filter((store) => selectedStores.includes(store._id ?? '')), [stores, selectedStores]);

    const onCloseDrawer = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    const onSubmit = useCallback(async () => {
        try {
            setLoading(true);
            const payload = {
                name,
                type: contractType,
                stores: selectedStores,
                expirationDate,
                price: Number(price) || 0
            };

            if (editMode && contractData) {
                await updateContract(contractData._id, payload);
            } else {
                await createContract(payload);
            }
            onCloseDrawer();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [name, contractType, selectedStores, expirationDate, price, createContract, updateContract, editMode, contractData, onCloseDrawer]);

    const estimatedInvestment = (Number(price) || 0) * selectedStores.length;

    return {
        name,
        setName,
        contractType,
        setContractType,
        loading,
        isStoreModalOpen,
        setIsStoreModalOpen,
        selectedStores,
        setSelectedStores,
        expirationDate,
        setExpirationDate,
        price,
        setPrice,
        filteredStores,
        estimatedInvestment,
        today: dayjs(),
        onCloseDrawer,
        onSubmit
    };
}

/** Búsqueda por nombre o cliente en el modal de selección de pisos. */
export function useStoreSearch(stores) {
    const [filteredStores, setFilteredStores] = React.useState(stores || []);
    const [searchText, setSearchText] = React.useState('');

    React.useEffect(() => {
        setFilteredStores(stores || []);
    }, [stores]);

    const handleSearch = (value) => {
        setSearchText(value);
        if (!value) {
            setFilteredStores(stores || []);
            return;
        }

        const filtered =
            stores?.filter(
                (store) => store.name?.toLowerCase().includes(value.toLowerCase()) || store.clientName?.toLowerCase().includes(value.toLowerCase())
            ) || [];
        setFilteredStores(filtered);
    };

    return { filteredStores, searchText, handleSearch };
}
